import { FormHelperText, Grid, FormControl, FormControlLabel, Checkbox, MenuItem, InputLabel, OutlinedInput, 
  Box, Stack, Button, Typography, Link, Card, CardContent} from "@mui/material";
import { useFormik } from "formik";
import InfoIcon from "@mui/icons-material/Info";
import { useRecordContext, useEditContext, useDeleteWithConfirmController } from "ra-core";
import { FC, useCallback, useEffect } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import * as Yup from 'yup';
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import Confirm from "@/components/widgets/Confirm";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { CustomKeyVaultDto, CustomKeyVaultInput } from "@/gen/api/securefile";
import { CloudProvider } from "@/gen/api/user";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import { useRevealPass } from "@/utils/useRevealPass";
import SelectField from "@/components/formik/SelectField";
import Edit from "@/controller/Edit";
import TextField from "@/components/formik/TextField";



const validationSchema =
  Yup.object().shape({
    cloudProvider: Yup.number().when('isEnabled', {
      is: (isEnabled: boolean) => isEnabled,
      then: () => Yup.number().required("Cloud Provider is a required field")
    }),
    tenantId: Yup.string().when('isEnabled', {
      is: (isEnabled: boolean) => isEnabled,
      then: () => Yup.string().max(100, 'Tenant Id must be at most 100 characters').required("Tenant Id is a required field")
    }),
    clientId: Yup.string().when('isEnabled', {
      is: (isEnabled: boolean) => isEnabled,
      then: () => Yup.string().max(100, 'Client Id must be at most 100 characters').required("Client Id is a required field")
    }),
    clientSecret: Yup.string().when('isEnabled', {
      is: (isEnabled: boolean) => isEnabled,
      then: () => Yup.string().max(65536, 'Client Secret must be at most 65536 characters').required("Client Secret is a required field")
    }),
    uri: Yup.string().when('isEnabled', {
      is: (isEnabled: boolean) => isEnabled,
      then: () => Yup.string().max(100, 'Uri must be at most 100 characters').required("Uri is a required field")
    }),
  })
const maskedString = '************************'

const customKeyResource = 'settings-clients-securefile-customkey-config'

const CustomKeyVaultSettings : FC = () => {
  const savedHandlers = useDefaultSavedHandlers("Save SecureFile Settings")

  return (
    <Edit resource={customKeyResource} basePath="" id="edit" {...savedHandlers} mutationMode="pessimistic">
      <CustomKeyVaultSettingsBody />
    </Edit>
  )
}

const CustomKeyVaultSettingsBody : FC = () => {
  const record : CustomKeyVaultDto|undefined = useRecordContext() as any
  const save = useSimpleSaveAction()
  const { saving, refetch } = useEditContext()

  const initialValues = {
    submit: '',
    isEnabled: record?.isEnabled ?? false,
    cloudProvider: record?.cloudProvider ?? '' as CloudProvider|'',
    tenantId: record?.tenantId ?? '',
    clientId: record?.clientId ?? '',
    clientSecret: record?.clientId ? maskedString : '',
    uri: record?.uri ?? '',
  }

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  })=>{
    const payload: CustomKeyVaultInput = {...values} as any
    if (payload.clientSecret === maskedString) { delete payload.clientSecret }
    save(payload, {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
        refetch && refetch()
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        const errMsg = retrieveAxiosErr(err)
        setErrors({ submit: errMsg })
        setSubmitting(false)
      },
    })
  }, [save, refetch])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { values, errors, touched, handleChange, handleBlur, setFieldTouched, resetForm, 
    submitForm, setStatus, setErrors, setSubmitting } = formikCtrl
  useEffect(() => {
    if (record) {
      resetForm({
        values: {
          submit: '',
          isEnabled: record.isEnabled ?? false,
          cloudProvider: record.cloudProvider ?? '' as CloudProvider|'',
          tenantId: record.tenantId ?? '',
          clientId: record.clientId ?? '',
          clientSecret: record?.clientId ? maskedString : '',
          uri: record.uri ?? '',
        }
      })
    }
  }, [record, resetForm])
  
  const { showPass, endAdornment } = useRevealPass()
  const delHandlers = useDefaultSavedHandlers('Disable Custom Key Vault')
  delHandlers.onSuccess.merge(useCallback(() => {
    refetch && refetch()
  }, [refetch]))
  delHandlers.onFailure.merge(useCallback((err:any) => {
    setStatus({ success: false })
    const errMsg = retrieveAxiosErr(err)
    setErrors({ submit: errMsg })
    setSubmitting(false)
  }, [setErrors, setStatus, setSubmitting]))
  const {
    open: delConfirmOpen,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
    loading: delSaving,
  } = useDeleteWithConfirmController({
    resource: customKeyResource,
    record: (record as any),
    mutationMode: 'pessimistic',
    ...delHandlers,
  });
  
  const history = useHistory()
  useEffect(() => {
    const unregister = history.block((newLocation, a) => {
      const pass = Object.keys(touched ?? {}).length === 0
      return a === 'PUSH' && !pass ? 'There are unsaved edits, discard edits and continue?' : undefined
    })
    return () => {
      unregister()
    }
  }, [history, touched])
  
  return (
    <>
      {errors.submit && (
        <Box sx={{ mt: 3 }}>
          <FormHelperText error>
            {errors.submit}
          </FormHelperText>
        </Box>
      )}
      <Stack direction="row" sx={{ pb: 2 }} >
        <Button sx={{ ml: 'auto', mr: 2 }}
          color="primary"
          variant="outlined"
          onClick={() => { 
            resetForm({touched:undefined}) 
          }}
        >
          Discard
        </Button>
        <Button
          color="primary"
          disabled={Object.keys(touched ?? {}).length === 0 || saving || delSaving}
          type="submit"
          variant="contained"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault()
            if (values.isEnabled) {
              submitForm()
            } else {
              handleDialogOpen(e)
            }
            return false
          }}
        >
          Save
        </Button>
        <Confirm
          isOpen={delConfirmOpen}
          loading={delSaving}
          valid={!values.isEnabled}
          title={"Disable Custom Key Vault"}
          onConfirm={handleDelete}
          onClose={handleDialogClose}
          content={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon fontSize="large" sx={{ color: 'info.main' }}/>
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1 }}
              >
                Disable Custom Key Vault to use encryption keys managed by OnData?
              </Typography>
            </Box>
          }
        />
      </Stack>

      <Card>
        <CardContent>
          <OdForm formikCtrl={formikCtrl}>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <FormControl>
                  <FormControlLabel 
                    control={
                      <Checkbox color="primary" name="isEnabled"
                        checked={values.isEnabled}
                        onChange={(e:any) => {
                          setFieldTouched('isEnabled', true)
                          handleChange(e)
                        }}
                      />
                    }
                    label="Enable Custom Key Vault"
                  />
                  <FormHelperText>
                    Refer to {" "}
                    <Link component={RouterLink} variant="caption" underline="always" 
                      to="/documents/ondata_portal/custom_key_vault">
                      documentation</Link> for more about Custom Key Vault.
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <SelectField label="Cloud Provider*" name="cloudProvider" disabled={!values.isEnabled}>
                  <MenuItem key={CloudProvider.Azure} value={CloudProvider.Azure}>Azure</MenuItem>
                  <MenuItem key={CloudProvider.AzureUSGovernment} value={CloudProvider.AzureUSGovernment}>Azure US Government</MenuItem>
                </SelectField>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Tenant Id*" name="tenantId" disabled={!values.isEnabled} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Client Id*" name="clientId" disabled={!values.isEnabled} />
              </Grid>
              <Grid item xs={12}>
                <FormControl 
                  fullWidth 
                  variant="outlined"
                  error={Boolean(touched.clientSecret && errors.clientSecret)}
                >
                  <InputLabel htmlFor="outlined-adornment-password">Client Secret*</InputLabel>
                  <OutlinedInput id="outlined-adornment-password" name="clientSecret"
                    label="Client Secret*"
                    disabled={!values.isEnabled}
                    type={touched.clientSecret && showPass ? 'text' : 'password'}
                    value={values.clientSecret}
                    onBlur={handleBlur}
                    onChange={(e:any) => { setFieldTouched('clientSecret', true); handleChange(e)}}
                    endAdornment={touched.clientSecret ? endAdornment : null}
                    sx={{ '& input[type=password]::-ms-reveal': { display: 'none' } }}
                  />
                  <FormHelperText>{touched.clientSecret && errors.clientSecret}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Uri*" name="uri" disabled={!values.isEnabled} />
              </Grid>
            </Grid>
          </OdForm>
        </CardContent>
          
      </Card>
    </>
  )
}


export default CustomKeyVaultSettings;
